import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { useGetList } from 'ra-core';

/**
 * OutstandingInvoicesWidget
 *
 * Displays a summary of unpaid customer invoices grouped into:
 * - Overdue (due_date < today, not paid)
 * - Due this week
 * - Total outstanding (all unpaid)
 */
export const OutstandingInvoicesWidget = () => {
    const { data: invoices, isPending } = useGetList('customer_invoices', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });

    if (isPending) {
        return (
            <Card className="border-none shadow-sm">
                <CardContent className="p-6 flex items-center justify-center h-36">
                    <div className="text-slate-400 text-sm animate-pulse">Loading invoices…</div>
                </CardContent>
            </Card>
        );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekEnd = new Date(today);
    weekEnd.setDate(today.getDate() + 7);

    const unpaid = (invoices ?? []).filter(
        (inv) => inv.status !== 'paid' && inv.status !== 'cancelled'
    );

    const overdue = unpaid.filter(
        (inv) => inv.due_date && new Date(inv.due_date) < today
    );
    const dueSoon = unpaid.filter((inv) => {
        if (!inv.due_date) return false;
        const d = new Date(inv.due_date);
        return d >= today && d <= weekEnd;
    });

    const overdueAmount = overdue.reduce((s, inv) => s + ((inv.total_amount || 0) - (inv.paid_amount || 0)), 0);
    const dueSoonAmount = dueSoon.reduce((s, inv) => s + ((inv.total_amount || 0) - (inv.paid_amount || 0)), 0);
    const totalOutstanding = unpaid.reduce((s, inv) => s + ((inv.total_amount || 0) - (inv.paid_amount || 0)), 0);

    const fmt = (n: number) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

    return (
        <Card className="border-none shadow-sm bg-card">
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" /> Outstanding Invoices
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
                {/* Overdue */}
                <div className={`flex items-center justify-between p-3 rounded-lg border ${
                    overdue.length > 0 ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'
                }`}>
                    <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${overdue.length > 0 ? 'text-red-500' : 'text-slate-300'}`} />
                        <div>
                            <p className="text-xs font-bold text-slate-700">Overdue</p>
                            <p className="text-[10px] text-slate-400">{overdue.length} invoice{overdue.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <span className={`text-sm font-black ${overdue.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {fmt(overdueAmount)}
                    </span>
                </div>

                {/* Due this week */}
                <div className={`flex items-center justify-between p-3 rounded-lg border ${
                    dueSoon.length > 0 ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'
                }`}>
                    <div className="flex items-center gap-2">
                        <Clock className={`w-4 h-4 ${dueSoon.length > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
                        <div>
                            <p className="text-xs font-bold text-slate-700">Due this week</p>
                            <p className="text-[10px] text-slate-400">{dueSoon.length} invoice{dueSoon.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <span className={`text-sm font-black ${dueSoon.length > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {fmt(dueSoonAmount)}
                    </span>
                </div>

                {/* Total outstanding */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500" />
                        <div>
                            <p className="text-xs font-bold text-slate-700">Total Outstanding</p>
                            <p className="text-[10px] text-slate-400">{unpaid.length} unpaid</p>
                        </div>
                    </div>
                    <span className="text-sm font-black text-blue-700">{fmt(totalOutstanding)}</span>
                </div>
            </CardContent>
        </Card>
    );
};
